import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  ShieldCheck,
  Lock,
  User,
  MessageSquare,
  Smartphone,
} from "lucide-react";

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();

  const sections = [
    {
      icon: <Lock className="w-6 h-6 text-cyan-200" />,
      title: "Privacy Notice",
      content: [
        `You must be at least 18 years of age to use AICA HEALTH Services. Caregivers who are at least 18 years old may create an account on behalf of a minor, but minors may not independently use AICA HEALTH services.`,

        `Users may not misuse AICA HEALTH services or applications owned by AICA HEALTH TECH Pvt Ltd. Posting or transmitting illegal, obscene, threatening, defamatory, harmful, privacy-invasive, or spam-related content is strictly prohibited.`,

        `AICA HEALTH services and applications may only be used in compliance with applicable laws. Any violation of these terms may result in suspension or termination of access and licenses granted by AICA HEALTH TECH Pvt Ltd.`,
      ],
    },

    {
      icon: <User className="w-6 h-6 text-emerald-200" />,
      title: "Your Account",
      content: [
        `Certain services require an active AICA HEALTH account and a valid payment method. Users agree to maintain accurate, current, and complete account information.`,

        `You are responsible for maintaining the confidentiality of your account credentials and all activities performed under your account.`,

        `AICA HEALTH reserves the right to suspend, terminate, or restrict accounts and services at its sole discretion, including the removal or modification of content.`,
      ],
    },

    {
      icon: <MessageSquare className="w-6 h-6 text-pink-200" />,
      title: "Communications with AICA",
      content: [
        `By using AICA HEALTH services, you consent to receive electronic communications including emails, text messages, notifications, and account-related alerts.`,

        `These communications may include information regarding prescriptions, healthcare services, treatment updates, benefits, or reminders.`,

        `If you share your mobile device, email account, or AICA HEALTH account access with others, they may also have visibility into this information.`,
      ],
    },

    {
      icon: <Smartphone className="w-6 h-6 text-indigo-200" />,
      title: "App Permissions",
      content: [
        `Applications created and owned by AICA HEALTH TECH Pvt Ltd may request device permissions to improve functionality and user experience.`,

        `Users can manage, modify, or revoke permissions anytime through their device settings.`,
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-500 via-green-400 to-blue-300 relative overflow-hidden">

      {/* Background Glow */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500/20 blur-3xl rounded-full"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-emerald-500/20 blur-3xl rounded-full"></div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 py-10">

        {/* Header */}
        <div className="text-center mb-12">

          <div className="inline-flex items-center justify-center w-20 h-20 rounded-3xl bg-white/10 backdrop-blur-xl border border-white/10 shadow-2xl mb-6">
            <ShieldCheck className="w-10 h-10 text-white" />
          </div>

          <h1 className="text-4xl sm:text-6xl font-black text-white tracking-tight">
            Privacy Policy
          </h1>

          <p className="mt-4 text-white/80 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
            Learn how AICA HEALTH collects, protects, and manages your
            information while using our healthcare services and applications.
          </p>
        </div>

        {/* Sections */}
        <div className="grid gap-6">

          {sections.map((section, index) => (
            <div
              key={index}
              className="group relative overflow-hidden rounded-3xl border border-white/10 bg-black/15 backdrop-blur-2xl p-6 sm:p-8 shadow-2xl hover:bg-black/20 transition-all duration-500 hover:scale-[1.01]"
            >

              {/* Glow */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition duration-500 bg-gradient-to-r from-white/5 via-cyan-300/10 to-emerald-300/10"></div>

              <div className="relative z-10">

                {/* Title */}
                <div className="flex items-center gap-4 mb-5">

                  <div className="w-14 h-14 rounded-2xl bg-white/10 border border-white/10 flex items-center justify-center shadow-lg">
                    {section.icon}
                  </div>

                  <h2 className="text-2xl sm:text-3xl font-bold text-white">
                    {section.title}
                  </h2>
                </div>

                {/* Content */}
                <div className="space-y-4">

                  {section.content.map((paragraph, idx) => (
                    <p
                      key={idx}
                      className="text-white/90 leading-relaxed text-sm sm:text-base"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-5 border-t border-white/10 pt-8">

          <div>
            <h3 className="text-white font-semibold text-lg">
              AICA HEALTH
            </h3>

            <p className="text-white/70 text-sm">
              Secure AI-powered healthcare assistance platform.
            </p>
          </div>

          <button
            onClick={() => navigate("/")}
            className="group flex items-center gap-3 px-6 py-3 rounded-2xl bg-gradient-to-r from-cyan-300 to-emerald-300 text-[#071B2A] font-bold shadow-2xl hover:scale-105 transition-all duration-300"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Back to Home
          </button>
        </div>

      </div>
    </div>
  );
}